import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { IoShieldCheckmark, IoClose, IoSaveOutline } from 'react-icons/io5';

import {
  fetchRoles,
  getUserRoles,
  assignRoleToUser,
  revokeRoleFromUser,
} from '../../services/api';

export const UserRolesModal = ({ isOpen, onClose, user, userIdToken }) => {
  const [allRoles, setAllRoles] = useState([]);
  const [userAssignedRoles, setUserAssignedRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Checkbox toggles tracking for saving delta.
  // True means selected (to be assigned or already assigned), False means not selected
  const [selectedRoleIds, setSelectedRoleIds] = useState(new Set());

  useEffect(() => {
    if (isOpen && user) {
      loadInitialData();
    }
    // eslint-disable-next-line
  }, [isOpen, user]);

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      const userId = user.id || user._id || user.user_id;
      const [rolesRes, userRolesRes] = await Promise.all([
        fetchRoles(userIdToken),
        getUserRoles(userIdToken, userId),
      ]);

      setAllRoles(rolesRes.data || []);
      const userRoles = userRolesRes.data || [];
      setUserAssignedRoles(userRoles);

      // Populate the selected set with the IDs of currently assigned roles
      const initialSet = new Set(
        userRoles.map(
          (ur) => ur.role_id || (ur.role && (ur.role.id || ur.role._id)),
        ),
      );
      setSelectedRoleIds(initialSet);
    } catch (error) {
      toast.error(`Failed to load roles: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !user) return null;

  const handleToggleRole = (roleId) => {
    const newSet = new Set(selectedRoleIds);
    if (newSet.has(roleId)) {
      newSet.delete(roleId);
    } else {
      newSet.add(roleId);
    }
    setSelectedRoleIds(newSet);
  };

  const handleSave = async () => {
    setIsSaving(true);
    const originalAssignedIds = new Set(
      userAssignedRoles.map(
        (ur) => ur.role_id || (ur.role && (ur.role.id || ur.role._id)),
      ),
    );
    const userId = user.id || user._id || user.user_id;

    // Find which roles to add and which to remove
    const rolesToAdd = [...selectedRoleIds].filter(
      (id) => !originalAssignedIds.has(id),
    );
    const rolesToRemove = [...originalAssignedIds].filter(
      (id) => !selectedRoleIds.has(id),
    );

    try {
      // Execute all removals and additions in parallel mapping to promises
      const removePromises = rolesToRemove.map((roleId) =>
        revokeRoleFromUser(userIdToken, userId, roleId),
      );
      const addPromises = rolesToAdd.map((roleId) =>
        assignRoleToUser(userIdToken, userId, roleId),
      );

      await Promise.all([...removePromises, ...addPromises]);
      toast.success('User roles updated successfully');
      onClose();
    } catch (error) {
      toast.error(`Error updating roles: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex animate-fade-in items-center justify-center bg-black bg-opacity-60 p-4 backdrop-blur-sm">
      <div className="bg-1 border-line animate-fade-in-up flex max-h-[90vh] w-full max-w-lg flex-col rounded-xl border shadow-2xl">
        {/* Header */}
        <div className="border-line bg-2/30 flex shrink-0 items-center justify-between rounded-t-xl border-b p-5">
          <h3 className="text-p-text flex items-center gap-2 text-xl font-bold">
            <IoShieldCheckmark className="text-[#5652CF]" />
            Manage User Roles
          </h3>
          <button
            onClick={onClose}
            className="hover:bg-2 rounded-full p-1 transition-colors"
          >
            <IoClose className="text-s-text h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <div className="custom-scrollbar flex-1 overflow-y-auto p-6">
          <div className="mb-4">
            <p className="text-s-text text-sm">
              Assign roles and permissions to{' '}
              <strong className="text-p-text">{user.name}</strong> ({user.email}
              ).
            </p>
          </div>

          {isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#5652CF] border-t-transparent"></div>
            </div>
          ) : (
            <div className="space-y-3">
              {allRoles.length === 0 ? (
                <p className="text-s-text bg-2 rounded-lg p-4 text-center text-sm">
                  No roles defined in the system.
                </p>
              ) : (
                allRoles.map((role) => {
                  const roleId = role.id || role._id;
                  const isSelected = selectedRoleIds.has(roleId);

                  return (
                    // eslint-disable-next-line
                    <div
                      key={roleId}
                      onClick={() => handleToggleRole(roleId)}
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border-2 p-4 transition-colors ${isSelected ? 'border-[#5652CF] bg-[#5652CF]/5' : 'border-line bg-2/50 hover:border-s-text'}`}
                    >
                      <div className="pt-0.5">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}} // Handled by div wrapper
                          className="bg-1 border-line h-4 w-4 rounded text-[#5652CF] focus:ring-2 focus:ring-[#5652CF]"
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-p-text text-sm font-bold">
                            {role.name}
                          </span>
                          {role.is_system_role && (
                            <span className="rounded border border-purple-500/20 bg-purple-500/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-purple-400">
                              System
                            </span>
                          )}
                        </div>
                        <p className="text-s-text mt-1 text-xs">
                          {role.description || 'No description provided.'}
                        </p>
                        <p className="mt-1.5 text-xs font-medium text-blue-400">
                          {role.permissions?.length || 0} permissions granted
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-line bg-2/30 flex shrink-0 items-center justify-end gap-3 rounded-b-xl border-t p-5">
          <button
            onClick={onClose}
            className="text-s-text border-line hover:bg-2 hover:text-p-text rounded-lg border bg-transparent px-5 py-2.5 text-sm font-semibold transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || isLoading}
            className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-500/20 transition-all hover:-translate-y-0.5 hover:bg-[#5652CF] disabled:cursor-wait disabled:opacity-50"
          >
            <IoSaveOutline className="text-lg" />
            {isSaving ? 'Saving...' : 'Save Roles'}
          </button>
        </div>
      </div>
    </div>
  );
};
